import {STORAGE_ALLOWLIST} from 'common/gdpr-constants';

export {STORAGE_ALLOWLIST};

const warnSet = new Set();
const warnDisabled = (storageType, key) => {
  // attempt to balance spamming the console with providing relevant information
  if(!warnSet.has(`${storageType}:${key}`)) {
    console.warn(`${storageType} disabled due to GDPR opt out`);
    warnSet.add(`${storageType}:${key}`);
  }
};
/**
 * copy & past from iron/common/util
 *
 * Return a copy of an array with duplicate items removed
 * @param {Array} arr - array to de-duplicate
 * @param {function} [options.hash] - hashes each element for comparison
 * Items are compared via strict equality so duplicate arrays/objects will not be removed
 * unless a `hash` function is specified to hash objects before comparison.
 * @example
 * unique([{'a': 1, 'b': 2}, {'a': 1, 'b': 3}, {'a': 1, 'b': 2}], {hash: o => `${o['a']}:${o['b']}`});
 * // [{'a': 1, 'b': 2}, {'a': 1, 'b': 3}]
 */
function unique(arr, options = {}) {
  let hashed;
  const encountered = new Set();
  return arr.filter((item) => {
    hashed = options.hash ? options.hash(item) : item;
    return encountered.has(hashed) ? false : encountered.add(hashed);
  });
}

// check whether a storage key should be excluded from GDPR disable
function isStorageKeyAllowed(key) {
  return STORAGE_ALLOWLIST.some((k) =>
    key.toLowerCase().includes(k.toLowerCase())
  );
}

/* classes used to disable localStorage/sessionStorage globally
 * original functionality can be re-enabled by calling:
 *
 *   window.localStorage.restore();
 *   window.sessionStorage.restore();
 */
class DummyStorage {
  constructor() {
    this.length = 0;
    this.original = window[this.storageType];
    delete window[this.storageType];
    window[this.storageType] = this;
  }

  getItem(key) {
    if (isStorageKeyAllowed(key)) {
      return this.original.getItem(key);
    } else {
      warnDisabled(this.storageType, key);
      return null;
    }
  }

  setItem(key, value) {
    if (isStorageKeyAllowed(key)) {
      this.original.setItem(key, value);
    } else {
      warnDisabled(this.storageType, key);
    }
  }

  removeItem(key) {
    if (isStorageKeyAllowed(key)) {
      this.original.removeItem(key);
    } else {
      warnDisabled(this.storageType, key);
    }
  }

  restore() {
    window[this.storageType] = this.original;
  }
}

class DummyLocalStorage extends DummyStorage {
  get storageType() {
    return `localStorage`;
  }
}

class DummySessionStorage extends DummyStorage {
  get storageType() {
    return `sessionStorage`;
  }
}

// disable localStorage by replacing it with "dummy" storage object
function deactivateLocalStorage() {
  try { // handle browsers without localStorage gracefully
    // clear non-allowed items in localStorage
    Object.keys(window.localStorage).forEach((key) => {
      if (!isStorageKeyAllowed(key)) {
        window.localStorage.removeItem(key);
      }
    });
    window.localStorage = new DummyLocalStorage();
  } catch (err) {
    // pass
  }
}

// disable sessionStorage by replacing it with "dummy" storage object
function deactivateSessionStorage() {
  try { // handle browsers without sessionStorage gracefully
    // clear non-allowed items in sessionStorage
    Object.keys(window.localStorage).forEach((key) => {
      if (!isStorageKeyAllowed(key)) {
        window.sessionStorage.removeItem(key);
      }
    });
    window.sessionStorage = new DummySessionStorage();
  } catch (err) {
    // pass
  }
}

// re-enable localStorage if it was previously disabled
function activateLocalStorage() {
  if (window.localStorage && window.localStorage.restore) {
    window.localStorage.restore();
  }
}

// re-enable sessionStorage if it was previously disabled
function activateSessionStorage() {
  if (window.sessionStorage && window.sessionStorage.restore) {
    window.sessionStorage.restore();
  }
}

/* get all variations of path/domain so that cookies with any value for either can be cleared
 * e.g.
 *
 * getVariations('foo.bar', '.', Array.prototype.pop)
 * >>> [".foo.bar", "foo.bar.", "foo.bar", ".foo", "foo.", "foo", ".", "", null]
 *
 * getVariations('/foo/bar', '/', Array.prototype.shift)
 * >>> ["/foo/bar/", "/foo/bar", "foo/bar/", "foo/bar", "/bar", "bar/", "bar", "/", "", null]
 */
function getVariations(string, splitChar, modifier) {
  const parts = string.split(splitChar);
  let variations = Array(parts.length).fill(null).map(() => {
    const variation = parts.join(splitChar);
    modifier.call(parts);
    return [
      ...(variation.startsWith(splitChar) ? [] : [`${splitChar}${variation}`]),
      ...(variation.endsWith(splitChar) ? [] : [`${variation}${splitChar}`]),
      variation,
    ];
  });
  variations = [].concat(...variations); // flatten
  variations = variations.concat([splitChar, ``, null]); // add extra variations
  return unique(variations);
}

// convert name, expiry, domain, and path into a valid cookie string and delete it
function deleteCookie(cookieName, expiryDate, domain=null, path=null) {
  const parts = [
    `${encodeURIComponent(cookieName)}=`,
    `expires=${expiryDate.toGMTString()}`,
  ];
  if (domain !== null) {
    parts.push(`domain=${domain}`);
  }
  if (path !== null) {
    parts.push(`path=${path}`);
  }
  document.cookie = parts.join(`; `);
}

// delete all cookies under the current domain (and all of its subdomains and paths)
function clearCookies() {
  document.cookie.split(`; `).forEach((cookie) => {
    const cookieName = cookie.split(`;`)[0].split(`=`)[0];
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() - 1); // 1 day ago

    if (!isStorageKeyAllowed(cookieName)) {
      getVariations(window.location.hostname, `.`, Array.prototype.shift).forEach((domain) =>
        getVariations(window.location.pathname, `/`, Array.prototype.pop).forEach((path) =>
          deleteCookie(cookieName, expiryDate, domain, path)
        )
      );
    }
  });
}

export function deactivateStorage() {
  clearCookies();
  deactivateLocalStorage();
  deactivateSessionStorage();
}

export function activateStorage() {
  activateLocalStorage();
  activateSessionStorage();
}
